import { useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const DisclaimerPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', title: '免責事項' },
    title: '免責事項',
    sections: {
      nature: {
        title: 'サイトの性格について',
        important: '重要：個人的な実験ノートです',
        p1: '「せきそう NAG ノート」は、管理人が個人的に行っている3Dプリンターの実験や研究の記録をまとめたノートサイトです。学術研究機関や企業の公式な技術資料ではありません。',
        p2: '掲載している情報は、管理人の限られた環境・条件下での実験結果であり、すべての環境や用途において同じ結果が得られることを保証するものではありません。'
      },
      accuracy: {
        title: '情報の正確性について',
        p1: '当サイトに掲載している情報は、管理人が実際に行った実験や経験に基づくものですが、以下の点にご注意ください：',
        items: [
          '測定機器や環境による誤差が含まれる可能性があります',
          '使用している機材や材料のロット差による影響があります',
          '主観的な評価や感想が含まれています',
          '実験回数や統計的な処理は限定的です',
          '技術の進歩や新しい情報により、内容が古くなる場合があります'
        ],
        p2: '情報の正確性については可能な限り注意を払っておりますが、完全性や最新性を保証するものではありません。'
      },
      responsibility: {
        title: '利用者の自己責任',
        important: '重要な注意事項',
        p1: '当サイトの情報を利用した結果について、管理人は一切の責任を負うことができません。以下のようなケースを含め、すべて利用者の自己責任でご利用ください：',
        items: [
          '3Dプリンターの故障や損傷',
          '造形物の不具合や期待する性能が得られない場合',
          '材料の無駄遣いや経済的損失',
          '実験中の事故やケガ',
          'その他、当サイト利用に起因する直接的・間接的な損害'
        ],
        p2: '特に、高温や化学物質を扱う実験については、十分な安全対策を講じ、ご自身の判断と責任で実施してください。'
      },
      links: {
        title: '外部リンク・アフィリエイトリンクについて',
        p1: '当サイトには、外部サイトへのリンクやアフィリエイトリンクが含まれています：',
        affiliate: {
          title: 'アフィリエイトリンクについて',
          p1: '当サイトのリンクには、アフィリエイトリンクが含まれている場合があります。リンク経由の購入により、運営者に報酬が発生することがあります。',
          items: [
            '商品の価格に影響はありません',
            '報酬の有無にかかわらず、実際に使用した製品のみを紹介しています',
            'リンク先のサイトや商品については、各サイトの規約をご確認ください'
          ]
        },
        external: {
          title: '外部サイトについて',
          items: [
            'リンク先サイトの内容やサービスについて、管理人は責任を負いません',
            '各サイトの利用規約・プライバシーポリシーを必ずご確認ください',
            'リンク先での購入や契約は、各サイトとの直接的な取引となります'
          ]
        }
      },
      changes: {
        title: '免責事項の変更について',
        p1: '当免責事項は、必要に応じて変更する場合があります。変更した場合は、当ページに掲載することによって通知いたします。',
        p2: '継続してサイトをご利用いただく場合は、変更後の免責事項にご同意いただいたものとみなします。',
        updated: '最終更新日：2025年12月'
      },
      contact: {
        title: 'お問い合わせ',
        p1: '当免責事項に関するご質問は、',
        link: 'お問い合わせページ',
        p2: 'からご連絡ください。',
        p3: 'なお、技術的な内容については個別のサポートは行っておりません。あくまで参考情報としてご活用ください。'
      }
    }
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', title: 'Disclaimer' },
    title: 'Disclaimer',
    sections: {
      nature: {
        title: 'About the Nature of This Site',
        important: 'Important: This is a Personal Experiment Notebook',
        p1: '"Sekisou NAG Note" is a notebook site that compiles records of 3D printer experiments and research conducted personally by the administrator. It is not official technical documentation from an academic research institution or company.',
        p2: 'The information published on this site is based on experimental results under the administrator\'s limited environment and conditions, and does not guarantee that the same results will be obtained in all environments or applications.'
      },
      accuracy: {
        title: 'About Information Accuracy',
        p1: 'The information published on this site is based on experiments and experiences actually conducted by the administrator, but please note the following:',
        items: [
          'Errors due to measurement equipment or environment may be included',
          'There may be effects from lot differences in equipment and materials used',
          'Subjective evaluations and impressions are included',
          'The number of experiments and statistical processing is limited',
          'Content may become outdated due to technological advances or new information'
        ],
        p2: 'While we pay as much attention as possible to the accuracy of information, we do not guarantee completeness or currency.'
      },
      responsibility: {
        title: 'User\'s Own Responsibility',
        important: 'Important Notice',
        p1: 'The administrator cannot assume any responsibility for the results of using the information on this site. Please use at your own risk, including but not limited to the following cases:',
        items: [
          '3D printer failure or damage',
          'Defects in printed objects or failure to achieve expected performance',
          'Waste of materials or financial loss',
          'Accidents or injuries during experiments',
          'Other direct or indirect damages arising from the use of this site'
        ],
        p2: 'In particular, for experiments involving high temperatures or chemicals, please take sufficient safety measures and conduct them at your own judgment and responsibility.'
      },
      links: {
        title: 'About External Links and Affiliate Links',
        p1: 'This site contains links to external sites and affiliate links:',
        affiliate: {
          title: 'About Affiliate Links',
          p1: 'Links on this site may include affiliate links. Purchases made through these links may generate compensation for the operator.',
          items: [
            'There is no impact on product prices',
            'We only introduce products that have been actually used, regardless of compensation',
            'Please check the terms of each site for linked sites and products'
          ]
        },
        external: {
          title: 'About External Sites',
          items: [
            'The administrator is not responsible for the content or services of linked sites',
            'Please be sure to check the terms of use and privacy policy of each site',
            'Purchases or contracts at linked sites are direct transactions with those sites'
          ]
        }
      },
      changes: {
        title: 'About Changes to This Disclaimer',
        p1: 'This disclaimer may be changed as necessary. If changed, we will notify you by posting on this page.',
        p2: 'If you continue to use the site, you will be deemed to have agreed to the revised disclaimer.',
        updated: 'Last updated: December 2025'
      },
      contact: {
        title: 'Contact',
        p1: 'For questions regarding this disclaimer, please contact us via the ',
        link: 'contact page',
        p2: '.',
        p3: 'Please note that we do not provide individual support for technical content. Please use it as reference information only.'
      }
    }
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: isEnglish ? '/en' : '/' },
    { label: TEXT.breadcrumb.title, path: isEnglish ? '/en/disclaimer' : '/disclaimer' }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* ページタイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.title}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            <div className="metal-panel p-8 space-y-8">
              {/* サイトの性格 */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.nature.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <div className="bg-accent/10 border border-accent/30 p-4 rounded">
                    <p className="text-accent">
                      <i className="ri-information-line mr-2"></i>
                      <strong>{TEXT.sections.nature.important}</strong>
                    </p>
                  </div>
                  <p>{TEXT.sections.nature.p1}</p>
                  <p>{TEXT.sections.nature.p2}</p>
                </div>
              </section>

              {/* 情報の正確性について */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.accuracy.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <p>{TEXT.sections.accuracy.p1}</p>
                  <div className="bg-metal/30 p-4 border border-metal rounded">
                    <ul className="space-y-2">
                      {TEXT.sections.accuracy.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <p>{TEXT.sections.accuracy.p2}</p>
                </div>
              </section>

              {/* 利用者の自己責任 */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.responsibility.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <div className="bg-red-900/20 border border-red-500/30 p-4 rounded">
                    <p className="text-red-300">
                      <i className="ri-alert-line mr-2"></i>
                      <strong>{TEXT.sections.responsibility.important}</strong>
                    </p>
                  </div>
                  <p>{TEXT.sections.responsibility.p1}</p>
                  <div className="bg-metal/30 p-4 border border-metal rounded">
                    <ul className="space-y-2">
                      {TEXT.sections.responsibility.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <p>{TEXT.sections.responsibility.p2}</p>
                </div>
              </section>

              {/* 外部リンク・アフィリエイトについて */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.links.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <p>{TEXT.sections.links.p1}</p>
                  <div className="bg-metal/30 p-4 border border-metal rounded">
                    <h3 className="text-white font-semibold mb-3">{TEXT.sections.links.affiliate.title}</h3>
                    <p className="mb-3">{TEXT.sections.links.affiliate.p1}</p>
                    <ul className="space-y-1 text-xs">
                      {TEXT.sections.links.affiliate.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-metal/30 p-4 border border-metal rounded">
                    <h3 className="text-white font-semibold mb-3">{TEXT.sections.links.external.title}</h3>
                    <ul className="space-y-2">
                      {TEXT.sections.links.external.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* 免責事項の変更 */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.changes.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <p>{TEXT.sections.changes.p1}</p>
                  <p>{TEXT.sections.changes.p2}</p>
                  <p className="text-xs text-metal-light">{TEXT.sections.changes.updated}</p>
                </div>
              </section>

              {/* お問い合わせ */}
              <section>
                <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6">
                  {TEXT.sections.contact.title}
                </h2>
                <div className="space-y-4 text-metal-light text-sm leading-relaxed">
                  <p>
                    {TEXT.sections.contact.p1}
                    <a href="/contact" className="text-accent hover:underline">{TEXT.sections.contact.link}</a>
                    {TEXT.sections.contact.p2}
                  </p>
                  <p>{TEXT.sections.contact.p3}</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DisclaimerPage;